import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import GoogleCloudIcon from "src/components/ui/icons/googlecloud";
import { getProductName } from "src/utils/brandTheme";

import GcpCloudMonitoringInstructions from "./GcpCloudMonitoringInstructionsContent";
import GcpCloudMonitoringIntegrationForm from "./GcpCloudMonitoringIntegrationForm";

interface GcpCloudMonitoringIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

export default function GcpCloudMonitoringIntegration({ isOpen, onClose, onRefetch, brandTheme }: GcpCloudMonitoringIntegrationProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const productName = getProductName(brandTheme);

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  const handleFormCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <GoogleCloudIcon className="h-6 w-6" />
            Connect GCP Cloud Monitoring
          </DialogTitle>
          <DialogDescription>
            Connect your GCP project to give {productName} access to Cloud Monitoring and Cloud Logging.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <GcpCloudMonitoringInstructions productName={productName} />
            </div>
          </div>

          <GcpCloudMonitoringIntegrationForm
            onComplete={handleFormComplete}
            onCancel={handleFormCancel}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
